import React, { useState } from "react";
import PageShell from "@/components/PageShell";
import { FadeUp, RevealLines } from "@/components/Reveal";
import { submitContact } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";

const interests = [
  "API Intermediates supply",
  "Research collaboration",
  "Business development",
  "Careers",
  "General inquiry",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    interest: interests[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    try {
      setLoading(true);
      await submitContact(form);
      setDone(true);
      toast.success("Message received. We'll respond within two business days.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong.";
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell title="Contact — NewGenPharma">
      <section className="px-6 md:px-10 pt-16 md:pt-24 pb-16">
        <div className="mx-auto max-w-[1600px]">
          <FadeUp>
            <div className="font-mono-tight text-[11px] text-ink/60 flex items-center gap-4 mb-8">
              <span className="w-1.5 h-1.5 bg-oxred inline-block" />
              §04 · Contact
            </div>
          </FadeUp>
          <RevealLines
            as="h1"
            className="font-display text-[13vw] md:text-[8vw] leading-[0.92] tracking-[-0.03em]"
            lines={["Say hello,", <>send a <em className="italic text-oxred">dossier.</em></>]}
          />
        </div>
      </section>

      <section className="px-6 md:px-10 pb-32">
        <div className="mx-auto max-w-[1600px] grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Left column: contact info */}
          <FadeUp className="md:col-span-4">
            <div className="space-y-12">
              <div>
                <span className="font-mono-tight text-[10px] text-ink/60 block mb-3">Headquarters</span>
                <p className="text-ink text-base leading-relaxed">
                  NewGenPharma AG<br />
                  Baarerstrasse 78<br />
                  CH-6300 Zug, Switzerland
                </p>
              </div>
              <div>
                <span className="font-mono-tight text-[10px] text-ink/60 block mb-3">General</span>
                <a href="mailto:office@newgenpharma.ch" className="text-ink link-underline text-base">
                  office@newgenpharma.ch
                </a>
              </div>
              <div>
                <span className="font-mono-tight text-[10px] text-ink/60 block mb-3">Business Development</span>
                <a href="mailto:bd@newgenpharma.ch" className="text-ink link-underline text-base">
                  bd@newgenpharma.ch
                </a>
              </div>
              <div>
                <span className="font-mono-tight text-[10px] text-ink/60 block mb-3">Response time</span>
                <p className="text-ink/70 text-sm">
                  We respond to inquiries within two Swiss business days. Non-disclosure agreements are
                  available on request for technical discussions.
                </p>
              </div>
            </div>
          </FadeUp>

          {/* Right column: form */}
          <div className="md:col-span-8">
            {done ? (
              <FadeUp>
                <div
                  data-testid="contact-success"
                  className="border border-ink/15 p-12 md:p-20 flex flex-col items-start bg-bone"
                >
                  <CheckCircle2 size={36} className="text-oxred" />
                  <h2 className="font-display text-4xl md:text-5xl mt-6 tracking-tight leading-tight max-w-xl">
                    Received. Thank you, {form.name.split(" ")[0]}.
                  </h2>
                  <p className="mt-6 text-ink/70 max-w-md">
                    A member of our team will reply from a <span className="font-mono-tight text-[11px]">@newgenpharma.ch</span> address
                    within two business days.
                  </p>
                  <button
                    onClick={() => {
                      setDone(false);
                      setForm({ name: "", email: "", organization: "", interest: interests[0], message: "" });
                    }}
                    data-testid="contact-send-another"
                    className="mt-10 font-mono-tight text-[11px] border-b border-ink pb-1 inline-flex items-center gap-2"
                  >
                    Send another <ArrowUpRight size={14} />
                  </button>
                </div>
              </FadeUp>
            ) : (
              <FadeUp>
                <form onSubmit={submit} data-testid="contact-form" className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2">
                  <div>
                    <label className="font-mono-tight text-[10px] text-ink/60">Name*</label>
                    <input
                      className="field"
                      data-testid="contact-input-name"
                      value={form.name}
                      onChange={upd("name")}
                      placeholder="Dr. Jane Doe"
                      required
                    />
                  </div>
                  <div>
                    <label className="font-mono-tight text-[10px] text-ink/60">Email*</label>
                    <input
                      className="field"
                      data-testid="contact-input-email"
                      type="email"
                      value={form.email}
                      onChange={upd("email")}
                      placeholder="jane@example.ch"
                      required
                    />
                  </div>
                  <div>
                    <label className="font-mono-tight text-[10px] text-ink/60">Organization</label>
                    <input
                      className="field"
                      data-testid="contact-input-org"
                      value={form.organization}
                      onChange={upd("organization")}
                      placeholder="Institute / Group"
                    />
                  </div>
                  <div>
                    <label className="font-mono-tight text-[10px] text-ink/60">Interest</label>
                    <select
                      className="field"
                      data-testid="contact-input-interest"
                      value={form.interest}
                      onChange={upd("interest")}
                    >
                      {interests.map((i) => (
                        <option key={i} value={i}>{i}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2 mt-4">
                    <label className="font-mono-tight text-[10px] text-ink/60">Message*</label>
                    <textarea
                      className="field"
                      data-testid="contact-input-message"
                      rows={5}
                      value={form.message}
                      onChange={upd("message")}
                      placeholder="Tell us what you're looking for. Include compound classes, batch size, timelines."
                      required
                    />
                  </div>
                  <div className="md:col-span-2 mt-12 flex items-center justify-between border-t border-ink/15 pt-8">
                    <span className="font-mono-tight text-[10px] text-ink/60">
                      NGP · REPLY WITHIN 2 BUSINESS DAYS
                    </span>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="contact-submit-button"
                      className="group inline-flex items-center gap-3 px-8 py-4 bg-ink text-bone font-mono-tight text-[11px] disabled:opacity-50"
                    >
                      <span className="w-1.5 h-1.5 bg-oxred inline-block" />
                      {loading ? "Sending…" : "Send inquiry"}
                      <ArrowUpRight
                        size={14}
                        className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                      />
                    </button>
                  </div>
                </form>
              </FadeUp>
            )}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
